/**
 * 扫码枪 keyboard-wedge 输入检测的纯逻辑（Spec §37、02-ux-ia.md F9）——
 * 独立于 DOM/React 抽出，便于对"快速连续字符 + 回车"这条时序判据做单元测试
 * （计时类逻辑最容易在真实浏览器行为里出偏差，值得直接测）。
 */

/** 单字符最大间隔（ms）——快于人类正常打字速度，是扫码枪输入的判据 */
export const SCANNER_MAX_INTERVAL_MS = 50;
/** 少于这个长度不判定为扫码——避免单个按键的巧合触发 */
export const SCANNER_MIN_LENGTH = 3;

export interface ScannerBufferState {
  readonly buffer: string;
  readonly lastCharAt: number;
}

export const INITIAL_SCANNER_STATE: ScannerBufferState = { buffer: '', lastCharAt: 0 };

export interface ScannerKeyEvent {
  readonly key: string;
  readonly timestamp: number;
}

export interface ScannerStepResult {
  readonly next: ScannerBufferState;
  /** 非 null 表示这次按键（回车）完成了一次判定为扫码枪输入的序列 */
  readonly scanned: string | null;
}

/**
 * 单步状态转移：给定当前缓冲区状态与一次按键事件，返回下一状态，以及
 * （如果这次是构成一次完整扫码判定的回车键）扫描到的条码字符串。
 *
 * 判据：字符间隔 ≤ SCANNER_MAX_INTERVAL_MS 视为同一次扫码枪连续输入；
 * 一旦间隔变大，缓冲区从这个新字符重新开始（判定为人工正常打字的开始）。
 * 非单字符按键（Shift/Ctrl/箭头等）不参与缓冲区累积，也不重置计时——
 * 修饰键本身不构成"打字变慢"的证据。
 */
export function scannerStep(state: ScannerBufferState, event: ScannerKeyEvent): ScannerStepResult {
  if (event.key === 'Enter') {
    const scanned = state.buffer.length >= SCANNER_MIN_LENGTH ? state.buffer : null;
    return { next: { buffer: '', lastCharAt: event.timestamp }, scanned };
  }

  if (event.key.length !== 1) {
    return { next: state, scanned: null };
  }

  const gap = event.timestamp - state.lastCharAt;
  const buffer = gap > SCANNER_MAX_INTERVAL_MS ? event.key : state.buffer + event.key;
  return { next: { buffer, lastCharAt: event.timestamp }, scanned: null };
}

export function isTypingTarget(
  el: { tagName?: string; isContentEditable?: boolean } | null,
): boolean {
  if (!el) return false;
  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true;
  return el.isContentEditable === true;
}
